
/**
 * Formats quantities intelligently based on their size and unit
 * - Quantities >= 10: Round to whole numbers
 * - Quantities < 10: Show 1 decimal place max
 * - Very small quantities < 0.1: Round up to 0.1 minimum
 */
export const formatSmartQuantity = (quantityString: string): string => {
  if (!quantityString || quantityString.trim() === "") {
    return quantityString;
  }

  // Try to parse the quantity string (e.g., "1360.78 g" or "2.478 cups")
  const match = quantityString.trim().match(/^([\d.]+)\s*(.*)$/);
  
  if (!match) {
    // If we can't parse it, return the original string
    return quantityString;
  }

  const numericValue = parseFloat(match[1]);
  const unit = match[2].trim();

  // Handle invalid numbers
  if (isNaN(numericValue)) {
    return quantityString;
  }

  let formattedNumber: number;

  if (numericValue >= 10) {
    // Round to whole numbers for quantities >= 10
    formattedNumber = Math.round(numericValue);
  } else if (numericValue < 0.1) {
    // Round up very small quantities to 0.1 minimum
    formattedNumber = Math.max(0.1, Math.round(numericValue * 10) / 10);
  } else {
    // Show 1 decimal place for quantities between 0.1 and 10
    formattedNumber = Math.round(numericValue * 10) / 10;
  }

  // Format the number to avoid unnecessary decimals
  const formattedString = formattedNumber % 1 === 0 
    ? formattedNumber.toString() 
    : formattedNumber.toFixed(1);

  return unit ? `${formattedString} ${unit}` : formattedString;
};
